/**
 * Tracking Management Utility
 * Handles cookie consent and dynamic loading for:
 * - Google Analytics
 * - MS Clarity
 * - Apollo.io tracking
 */
import logger from './logger';

const CONSENT_COOKIE_NAME = 'movingwalls_market_consent';
const CONSENT_COOKIE_DAYS = 365;
const PRODUCTION_DOMAIN = import.meta.env.VITE_TRACKING_DOMAIN || 'market.movingwalls.com';
const APOLLO_APP_ID = import.meta.env.VITE_APOLLO_ID || '672b297166c08201b11dda6b';
const GA_ID = import.meta.env.VITE_GA_ID || 'G-MG8CY94Z3G';
const CLARITY_ID = import.meta.env.VITE_CLARITY_ID || 'tylpnb0g72';

/**
 * Set a cookie with name, value, and expiration days
 */
export function setCookie(name: string, value: string, days: number): void {
  console.log(`🔍 APOLLO DEBUG: [1] setCookie called - name: '${name}', value: '${value}', days: ${days}`);
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
}

/**
 * Check if user has already provided consent (accepted or declined)
 */
export function hasConsent(): boolean {
  const consent = getCookie(CONSENT_COOKIE_NAME);
  return consent === 'accepted' || consent === 'declined';
}

/**
 * Get the current consent status
 */
export function getConsentStatus(): 'accepted' | 'declined' | null {
  const consent = getCookie(CONSENT_COOKIE_NAME);
  if (consent === 'accepted' || consent === 'declined') {
    return consent;
  }
  return null;
}

/**
 * Save user's consent choice to cookie
 */
export function saveConsent(consent: 'accepted' | 'declined'): void {
  console.log(`🔍 APOLLO DEBUG: [16] saveConsent called with: '${consent}'`);
  setCookie(CONSENT_COOKIE_NAME, consent, CONSENT_COOKIE_DAYS);
  logger.log(`🍪 Cookie consent saved: ${consent}`);
}

/**
 * Check if we're on production domain
 */
export function isProductionDomain(): boolean {
  return window.location.hostname === PRODUCTION_DOMAIN;
}

function getTrackingContext() {
  return {
    protocol: window.location.protocol,
    host: window.location.host,
    hostname: window.location.hostname,
    origin: window.location.origin,
    href: window.location.href,
    expectedHost: PRODUCTION_DOMAIN,
    consent: getConsentStatus(),
  };
}

function logTrackingBlocked(provider: string, reason: 'domain_mismatch' | 'consent_not_accepted') {
  const context = getTrackingContext();
  console.log(
    `🚫 ${provider}: blocked (${reason}) | protocol=${context.protocol} | host=${context.host} | hostname=${context.hostname} | origin=${context.origin} | expected=${context.expectedHost} | consent=${context.consent ?? 'none'} | href=${context.href}`,
  );
}

/**
 * Dynamically load Apollo.io tracking script
 * Only loads on production domain and if consent is accepted
 */
export function loadApolloScript(): void {
  // Check if we're on production domain
  if (!isProductionDomain()) {
    logger.log('🚫 Apollo.io: Not loading on non-production domain');
    return;
  }

  // Check consent
  const consent = getConsentStatus();
  if (consent !== 'accepted') {
    logger.log('🚫 Apollo.io: User has not accepted tracking');
    return;
  }

  // Check if Apollo script is already loaded
  if (document.querySelector('script[src*="apollo.io"]')) {
    logger.log('✅ Apollo.io: Script already loaded');
    return;
  }

  logger.log('🚀 Apollo.io: Loading tracking script...');

  try {
    const randomStr = Math.random().toString(36).substring(7);
    const script = document.createElement('script');
    script.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${randomStr}`;
    script.async = true;
    script.defer = true;
    
    script.onload = function () {
      // @ts-ignore - Apollo tracking functions
      if (window.trackingFunctions && window.trackingFunctions.onLoad) {
        // @ts-ignore
        window.trackingFunctions.onLoad({ appId: APOLLO_APP_ID });
        logger.log('✅ Apollo.io: Tracking initialized');
      }
    };

    script.onerror = function () {
      logger.error('❌ Apollo.io: Failed to load tracking script');
    };

    document.head.appendChild(script);
  } catch (error) {
    logger.error('❌ Apollo.io: Error loading script', error);
  }
}

/**
 * Dynamically load Google Analytics tracking script
 * Only loads on production domain and if consent is accepted
 */
export function loadGoogleAnalyticsScript(): void {
  // Check if we're on production domain
  if (!isProductionDomain()) {
    logTrackingBlocked('Google Analytics', 'domain_mismatch');
    return;
  }

  // Check consent
  const consent = getConsentStatus();
  if (consent !== 'accepted') {
    logTrackingBlocked('Google Analytics', 'consent_not_accepted');
    return;
  }

  // Check if GA script is already loaded
  if (document.querySelector('script[src*="googletagmanager"]')) {
    logger.log('✅ Google Analytics: Script already loaded');
    return;
  }

  logger.log('🚀 Google Analytics: Loading tracking script...');

  try {
    // Initialize gtag function
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    function gtag() { window.dataLayer.push(arguments); }
    // @ts-ignore
    gtag('js', new Date());
    // @ts-ignore
    gtag('config', GA_ID);

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    
    script.onload = function () {
      logger.log('✅ Google Analytics: Tracking initialized');
    };

    script.onerror = function () {
      logger.error('❌ Google Analytics: Failed to load tracking script');
    };

    document.head.appendChild(script);
  } catch (error) {
    logger.error('❌ Google Analytics: Error loading script', error);
  }
}

/**
 * Dynamically load MS Clarity tracking script
 * Only loads on production domain and if consent is accepted
 */
export function loadClarityScript(): void {
  // Check if we're on production domain
  if (!isProductionDomain()) {
    logTrackingBlocked('MS Clarity', 'domain_mismatch');
    return;
  }

  // Check consent
  const consent = getConsentStatus();
  if (consent !== 'accepted') {
    logTrackingBlocked('MS Clarity', 'consent_not_accepted');
    return;
  }

  // Check if Clarity script is already loaded
  if (document.querySelector('script[src*="clarity.ms"]')) {
    logger.log('✅ MS Clarity: Script already loaded');
    return;
  }

  logger.log('🚀 MS Clarity: Loading tracking script...');

  try {
    // Standard Clarity SDK implementation with type safety
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
    
    script.onload = function () {
      logger.log('✅ MS Clarity: Tracking initialized');
    };

    script.onerror = function () {
      logger.error('❌ MS Clarity: Failed to load tracking script');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  } catch (error) {
    logger.error('❌ MS Clarity: Error loading script', error);
  }
}

/**
 * Initialize all tracking scripts based on existing consent
 * Call this on app startup
 */
export function initializeApolloTracking(): void {
  const consent = getConsentStatus();
  
  if (consent === 'accepted') {
    loadApolloScript();
    loadGoogleAnalyticsScript();
    loadClarityScript();
  } else if (consent === 'declined') {
    logger.log('🚫 Tracking: User has declined consent');
  } else {
    logger.log('⏳ Tracking: Waiting for user consent');
  }
}
